import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import CreatableSelect from 'react-select/creatable';
import { API_BASE } from '../utils/api';

const ModalEditarUsuario = ({ show, onHide, edit, setUsuarios, usuarios, cargos, isDemo }) => {
  const { id: logadoId, token } = useAuth();
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const [telefone, setTelefone] = useState('');
  const [cargo, setCargo] = useState(null);
  const [cargoExcecao, setCargoExcecao] = useState(null);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const clearPhone = useCallback((v) => v.replace(/\D/g, ''), []);
  const formatPhone = useCallback((v) => {
    const n = clearPhone(v || '');
    return n.length === 11 ? `(${n.slice(0, 2)}) ${n[2]} ${n.slice(3, 7)}-${n.slice(7)}` : v || '';
  }, [clearPhone]);

  useEffect(() => {
    if (edit && show) {
      setValue('nome', edit.nome);
      setValue('acesso', edit.acesso);
      setValue('gerente_percentual', edit.gerente_percentual || 0);
      setValue('condicao_tipo_conteudo', edit.condicao_tipo_conteudo || '');
      setCargo({ label: edit.cargo, value: edit.cargo });
      setCargoExcecao(edit.cargo_excecao ? { label: edit.cargo_excecao, value: edit.cargo_excecao } : null);
      setTelefone(formatPhone(edit.numero_telefone));
    }
  }, [edit, show, setValue, formatPhone]);

  const onSubmit = async (data) => {
    const payload = {
      nome: data.nome.toUpperCase(),
      cargo: (cargo?.value || '').toUpperCase(),
      cargo_excecao: (cargoExcecao?.value || '').toUpperCase() || null,
      condicao_tipo_conteudo: data.condicao_tipo_conteudo || null,
      numero_telefone: clearPhone(telefone),
      acesso: data.acesso,
      gerente_percentual: data.acesso === 'gerente' ? parseFloat(data.gerente_percentual || 0) : 0
    };

    if (parseInt(logadoId) === parseInt(edit.id) && data.senha) payload.senha = data.senha;

    setErro('');
    setLoading(true);
    if (isDemo) {
      setUsuarios(usuarios.map(u => u.id === edit.id ? { ...u, ...payload } : u));
      reset();
      setLoading(false);
      onHide();
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/users/${edit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setUsuarios(usuarios.map(u => u.id === edit.id ? { ...u, ...payload } : u));
        reset();
        onHide();
      } else {
        const result = await res.json();
        setErro(result.message || 'Erro ao salvar alterações.');
      }
    } catch (err) {
      setErro('Erro ao conectar com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  const cargoOptions = [...new Set(cargos)].sort().map(c => ({ label: String(c).toUpperCase(), value: String(c).toUpperCase() }));
  const tiposVideo = process.env.REACT_APP_OPTIONS_TIPOS?.split(',') || ['SHORT', 'LONGO'];
  const acessoAtual = watch('acesso');

  if (!edit) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title><i className="bi bi-pencil-square me-2"></i>Editar Usuário</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger" className="border-0 mb-3">{erro}</Alert>}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">NOME COMPLETO</Form.Label>
                <Form.Control {...register('nome', { required: true })} onChange={(e) => setValue('nome', e.target.value.toUpperCase())} value={watch('nome') || ''} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">TELEFONE</Form.Label>
                <Form.Control value={telefone} onChange={(e) => setTelefone(formatPhone(e.target.value))} />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">CARGO PRINCIPAL</Form.Label>
            <CreatableSelect isClearable options={cargoOptions} value={cargo} onChange={(opt) => setCargo(opt)} />
          </Form.Group>

          <div className="p-3 mb-3 rounded-3" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <h6 className="text-primary small fw-bold mb-3"><i className="bi bi-lightning-fill me-1"></i>REGRA DE EXCEÇÃO</h6>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="extra-small fw-bold text-muted">CARGO ESPECIAL</Form.Label>
                  <CreatableSelect isClearable options={cargoOptions} value={cargoExcecao} onChange={(opt) => setCargoExcecao(opt)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="extra-small fw-bold text-muted">CONDIÇÃO</Form.Label>
                  <Form.Select {...register('condicao_tipo_conteudo')}>
                    <option value="">NUNCA</option>
                    {tiposVideo.map(t => <option key={t} value={t.trim()}>{t.trim().toUpperCase()}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">NÍVEL DE ACESSO</Form.Label>
                <Form.Select {...register('acesso', { required: true })}>
                  <option value="admin">Admin</option>
                  <option value="analista">Analista</option>
                  <option value="colaborador">Colaborador</option>
                  <option value="gerente">Gerente</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {acessoAtual === 'gerente' && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">PERCENTUAL (%)</Form.Label>
                  <Form.Control type="number" step="0.1" {...register('gerente_percentual')} />
                </Form.Group>
              </Col>
            )}
          </Row>

          <Button type="submit" className="w-100 border-0 mt-2 py-2 fw-bold" style={{ backgroundColor: 'var(--brand-primary)' }} disabled={loading}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
              : <><i className="bi bi-check-lg me-2"></i>Salvar Alterações</>}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ModalEditarUsuario;